import { useAtomValue } from 'jotai';
import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { migratedStudentPng } from '../assets/images';
import { MIGRATED_MODAL_ASSET } from '../constants';
import { Language } from '../enum';
import { useDownloadFile } from '../hooks/use-download-file';
import { useEnvironment } from '../hooks/use-environment';
import { userAtom } from '../jotai-atoms/user.atom';
import { Icon } from './Icon';
import { Modal } from './Modal';

export interface ModalMigratedStudentProps {
  show: boolean;
  onClose: () => void;
}

export const ModalMigratedStudent: FC<ModalMigratedStudentProps> = ({ show, onClose }) => {
  const user = useAtomValue(userAtom);
  const { environment } = useEnvironment();
  const { download } = useDownloadFile();
  const __ = useTranslation().t;

  const handleSubmit = () => {
    const lang = (user?.lang || 'en') as Language;
    const url = MIGRATED_MODAL_ASSET[environment][lang];
    download(url);
    onClose();
  };

  return (
    <StyledModal
      submitButtonText={__('exploreMore')}
      onSubmit={handleSubmit}
      show={show}
      onClose={onClose}
      type="default"
      title="Congratulations!"
    >
      <StyledImage src={migratedStudentPng} alt="New platform" />

      <ImageText>{__('newPlatformForLearningEnglishFunFastEffective')}</ImageText>

      <SubTitle>{__('whatsNewPlatform')}</SubTitle>

      <ItemContainer>
        <StyledIcon name="check-circle" />
        <p>
          <Trans
            alt={__('updatedContentMoreLessonsExercises')}
            i18nKey="updatedContentMoreLessonsExercises"
          >
            Find <strong>updated content</strong> with more interactive lessons and exercises, with
            new pronunciation lessons.
          </Trans>
        </p>
      </ItemContainer>

      <ItemContainer>
        <StyledIcon name="check-circle" />
        <p>
          <Trans alt={__('newTypeOfLiveOnlineClasses')} i18nKey="newTypeOfLiveOnlineClasses">
            <strong>New type of live online classes</strong> with exciting challenges and more fun.
          </Trans>
        </p>
      </ItemContainer>

      <ItemContainer>
        <StyledIcon name="check-circle" />
        <p>
          <Trans alt={__('evenMoreAttractivePlatform')} i18nKey="evenMoreAttractivePlatform">
            An even more <strong>attractive platform</strong>, the content has been optimized to fit
            all device sizes.
          </Trans>
        </p>
      </ItemContainer>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  max-width: 520px;
`;

const StyledImage = styled.img`
  width: 100%;
  min-height: 150px;
  object-fit: contain;
`;

const ImageText = styled.p`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

const SubTitle = styled.p`
  margin-top: 16px;
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
`;

const ItemContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.green};
  margin-top: 6px;
  margin-right: 12px;
`;
